import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IconGif, IconFullscreen, IconRefresh, IconLoading, IconClose } from '@components/icons';
import { IconButton } from '@components/IconButton';
import { Button } from '@components/Button';
import { flavorConfig } from '@config/config';
import styles from './GifPreview.module.scss';

export interface GifPreviewProps {
  /** The site_id used to construct the GIF URL */
  siteId: string;
  /** Optional callback for regeneration */
  onRegenerate?: () => void;
  /** Whether regeneration is in progress */
  isRegenerating?: boolean;
  /** Cache-bust timestamp (changes when GIF is regenerated) */
  cacheBustKey?: number;
  /** Override the GIF URL (useful for demos/testing) */
  gifUrlOverride?: string;
}

const getGifUrl = (siteId: string, cacheBust?: number): string => {
  const base = `https://storage.googleapis.com/${flavorConfig.snapshotBucket}/webcapture/${siteId}/optimized.gif`;
  return cacheBust ? `${base}?t=${cacheBust}` : base;
};

export const GifPreview = ({
  siteId,
  onRegenerate,
  isRegenerating = false,
  cacheBustKey,
  gifUrlOverride,
}: GifPreviewProps) => {
  const { t } = useTranslation();
  const gifUrl = gifUrlOverride ?? getGifUrl(siteId, cacheBustKey);
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageLoadState, setImageLoadState] = useState<{
    url: string;
    status: 'loading' | 'loaded' | 'error';
  }>(() => ({ url: gifUrl, status: 'loading' }));
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Derive loading status: if the stored URL doesn't match current gifUrl, we're loading
  const imageStatus = imageLoadState.url === gifUrl ? imageLoadState.status : 'loading';

  // Preload image to detect load/error before rendering
  useEffect(() => {
    const img = new Image();
    img.onload = () => setImageLoadState({ url: gifUrl, status: 'loaded' });
    img.onerror = () => setImageLoadState({ url: gifUrl, status: 'error' });
    img.src = gifUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [gifUrl]);

  // Calculate popover position relative to trigger
  const updatePopoverPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPopoverPos({
      top: rect.bottom + 8,
      left: rect.left + rect.width / 2,
    });
  }, []);

  const showPopover = useCallback(() => {
    updatePopoverPosition();
    setIsPopoverVisible(true);
  }, [updatePopoverPosition]);

  const handleMouseEnter = () => {
    if (isPinned) return;
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    hoverTimeoutRef.current = setTimeout(showPopover, 300);
  };

  const handleMouseLeave = () => {
    if (isPinned) return;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    // Delay hide so user can move from trigger to popover
    leaveTimeoutRef.current = setTimeout(() => setIsPopoverVisible(false), 150);
  };

  // When mouse enters the popover, cancel the leave timeout
  const handlePopoverMouseEnter = () => {
    if (isPinned) return;
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
  };

  // When mouse leaves the popover, hide it
  const handlePopoverMouseLeave = () => {
    if (isPinned) return;
    leaveTimeoutRef.current = setTimeout(() => setIsPopoverVisible(false), 150);
  };

  const handleTriggerClick = () => {
    if (isPinned) {
      setIsPinned(false);
      setIsPopoverVisible(false);
    } else {
      setIsPinned(true);
      updatePopoverPosition();
      setIsPopoverVisible(true);
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    }
  };

  // Close pinned popover on click outside (trigger + popover)
  useEffect(() => {
    if (!isPinned) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = triggerRef.current?.contains(target);
      const inPopover = popoverRef.current?.contains(target);
      if (!inTrigger && !inPopover) {
        setIsPinned(false);
        setIsPopoverVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPinned]);

  const openFullscreen = () => {
    setIsPopoverVisible(false);
    setIsPinned(false);
    setIsModalOpen(true);
  };

  const handleExpandClick = () => {
    openFullscreen();
  };

  const handleImageClick = () => {
    openFullscreen();
  };

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Close modal on Escape
  useEffect(() => {
    if (!isModalOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCloseModal();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, handleCloseModal]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleCloseModal();
  };

  // Clean up timeouts
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
    };
  }, []);

  const renderPopoverContent = () => {
    if (isRegenerating) {
      return (
        <div className={styles.stateContainer}>
          <IconLoading size={24} />
          <span className={styles.stateText}>{t('gifPreview.regenerating')}</span>
        </div>
      );
    }

    if (imageStatus === 'loading') {
      return (
        <div className={styles.stateContainer}>
          <IconLoading size={24} />
        </div>
      );
    }

    if (imageStatus === 'error') {
      return (
        <div className={styles.stateContainer}>
          <IconGif size={32} color="var(--color-text-tertiary)" />
          <span className={styles.stateText}>{t('gifPreview.previewUnavailable')}</span>
          {onRegenerate && (
            <Button
              variant="text"
              size="small"
              onClick={onRegenerate}
              leadingIcon={<IconRefresh size={14} />}
            >
              {t('gifPreview.regenerate')}
            </Button>
          )}
        </div>
      );
    }

    return (
      <div className={styles.imageContainer}>
        <button
          type="button"
          className={styles.imageButton}
          onClick={handleImageClick}
          aria-label={t('gifPreview.expandPreview')}
        >
          <img
            src={gifUrl}
            alt={t('gifPreview.sitePreview')}
            className={styles.gifImage}
          />
        </button>
        <div className={styles.popoverActions}>
          <IconButton
            size="small"
            aria-label={t('gifPreview.expandPreview')}
            onClick={handleExpandClick}
            className={styles.expandButton}
          >
            <IconFullscreen size={14} />
          </IconButton>
        </div>
        {onRegenerate && (
          <div className={styles.popoverFooter}>
            <Button
              variant="text"
              size="small"
              onClick={onRegenerate}
              disabled={isRegenerating}
              leadingIcon={<IconRefresh size={14} />}
            >
              {t('gifPreview.regenerate')}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div
        ref={triggerRef}
        className={styles.trigger}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <IconButton
          size="small"
          aria-label={t('gifPreview.sitePreview')}
          onClick={handleTriggerClick}
          className={`${styles.triggerButton} ${isRegenerating ? styles.regenerating : ''}`}
        >
          <IconGif size={14} />
        </IconButton>
      </div>

      {/* Popover — portaled to body to escape overflow:hidden ancestors */}
      {isPopoverVisible && popoverPos && createPortal(
        <div
          ref={popoverRef}
          className={styles.popover}
          style={{ top: popoverPos.top, left: popoverPos.left }}
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
        >
          {renderPopoverContent()}
        </div>,
        document.body
      )}

      {/* Fullscreen modal — portaled to body */}
      {isModalOpen && createPortal(
        <div
          className={styles.backdrop}
          onClick={handleBackdropClick}
          role="presentation"
        >
          <div className={styles.modal} role="dialog" aria-modal="true">
            <div className={styles.modalHeader}>
              <span className={styles.modalTitle}>{t('gifPreview.sitePreview')}</span>
              <IconButton
                size="small"
                aria-label={t('gifPreview.closePreview')}
                onClick={handleCloseModal}
              >
                <IconClose size={18} />
              </IconButton>
            </div>
            <div className={styles.modalContent}>
              {isRegenerating ? (
                <div className={styles.stateContainer}>
                  <IconLoading size={32} />
                  <span className={styles.stateText}>{t('gifPreview.regenerating')}</span>
                </div>
              ) : imageStatus === 'error' ? (
                <div className={styles.stateContainer}>
                  <IconGif size={48} color="var(--color-text-tertiary)" />
                  <span className={styles.stateText}>{t('gifPreview.previewUnavailable')}</span>
                  {onRegenerate && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={onRegenerate}
                      leadingIcon={<IconRefresh size={14} />}
                    >
                      {t('gifPreview.regenerate')}
                    </Button>
                  )}
                </div>
              ) : (
                <img
                  src={gifUrl}
                  alt={t('gifPreview.sitePreview')}
                  className={styles.fullGif}
                />
              )}
            </div>
            {imageStatus === 'loaded' && onRegenerate && (
              <div className={styles.modalFooter}>
                <Button
                  variant="text"
                  size="small"
                  onClick={onRegenerate}
                  disabled={isRegenerating}
                  leadingIcon={<IconRefresh size={14} />}
                >
                  {t('gifPreview.regenerate')}
                </Button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
